import { createContext, useCallback, useContext, useState } from "react";
import { api } from "../lib/api";

// User data types (simplified version matching what we need)
export interface UserDetails {
	userType?: string;
	displayLanguage?: string;
	fertilityJourneyTypes?: string[];
	fertilityJourneyProgress?: unknown[];
	[key: string]: unknown;
}

export interface UserWithDetails {
	id: string;
	email: string;
	displayName?: string | null;
	userImage?: string | null;
	details?: UserDetails | null;
}

// Context type
interface UserDataContextType {
	userData: UserWithDetails | null;
	updateUserData: (newData: Partial<UserWithDetails>) => void;
	refetchUserData: () => Promise<void>;
}

// Create context with default values
const UserDataContext = createContext<UserDataContextType>({
	userData: null,
	updateUserData: () => {},
	refetchUserData: async () => {},
});

// Hook to use the context
export const useUserData = () => useContext(UserDataContext);

// Provider component
export function UserDataProvider({
	children,
	initialUserData,
}: {
	children: React.ReactNode;
	initialUserData: UserWithDetails | null;
}) {
	const [userData, setUserData] = useState<UserWithDetails | null>(
		initialUserData,
	);

	const updateUserData = (newData: Partial<UserWithDetails>) => {
		setUserData((prevData) => {
			// If no previous data, set the new data directly
			if (!prevData) return newData as UserWithDetails;
			// Otherwise merge with existing data
			return { ...prevData, ...newData };
		});
	};

	const refetchUserData = useCallback(async () => {
		try {
			const response = await api.get("/api/user");
			const profileData = response.data.user;
			const userDetails = response.data.details;

			setUserData({
				id: profileData.id,
				email: profileData.email,
				displayName: profileData.displayName || "User",
				userImage: profileData.userImage || null,
				details: userDetails || null,
			});
		} catch (error) {
			console.error("Error refetching user data:", error);
		}
	}, []);

	return (
		<UserDataContext.Provider
			value={{ userData, updateUserData, refetchUserData }}
		>
			{children}
		</UserDataContext.Provider>
	);
}
